import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Layout from "../../components/Layout";
import EntityHeader from "../../components/crm/EntityHeader";
import {
  ConfirmDelete,
  DateInput,
  ErrorBanner,
  Field,
  Modal,
  PrimaryButton,
  SecondaryButton,
  Select,
  TextArea,
  TextInput,
} from "../../components/crm/Form";
import { FieldCard } from "./CustomerDetail";
import { useAuth } from "../../contexts/AuthContext";
import api, { formatApiError } from "../../lib/api";

const WRITE_ROLES = ["owner", "ops"];
const COVERAGE_TYPES = [
  { value: "labor", label: "Labor" },
  { value: "parts", label: "Parts" },
  { value: "full", label: "Full" },
];
const PLATFORM_TYPES = [
  { value: "enphase", label: "Enphase" },
  { value: "solaredge", label: "SolarEdge" },
  { value: "tesla", label: "Tesla" },
  { value: "eg4", label: "EG4" },
  { value: "generic", label: "Generic" },
];

export default function WarrantyDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const canWrite = WRITE_ROLES.includes(user?.role);

  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [delBusy, setDelBusy] = useState(false);

  const load = async () => {
    setErr("");
    try {
      const { data } = await api.get(`/warranties/${id}`);
      setDoc(data);
    } catch (e) {
      setErr(formatApiError(e));
    }
  };
  useEffect(() => {
    load();
  }, [id]);

  const handleDelete = async () => {
    setDelBusy(true);
    try {
      await api.delete(`/warranties/${id}`);
      nav("/warranties");
    } catch (e) {
      setErr(formatApiError(e));
      setDelBusy(false);
    }
  };

  if (!doc)
    return (
      <Layout title="Warranty">
        <ErrorBanner message={err} />
      </Layout>
    );

  return (
    <Layout title={`Warranty · ${doc.manufacturer}`} subtitle="Coverage record">
      <EntityHeader
        overline="warranty"
        title={doc.manufacturer}
        breadcrumb={[
          { label: "Warranties", to: "/warranties" },
          { label: doc.manufacturer },
        ]}
        meta={
          <>
            <span>coverage · {doc.coverage_type}</span>
            <span>expires · {doc.expires}</span>
            {doc.platform_type && <span>platform · {doc.platform_type}</span>}
          </>
        }
        canEdit={canWrite}
        canDelete={canWrite}
        onEdit={() => setEditOpen(true)}
        onDelete={() => setDelOpen(true)}
      />

      <ErrorBanner message={err} />

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <FieldCard label="Manufacturer" value={doc.manufacturer} />
        <FieldCard label="Platform Type" value={doc.platform_type || "—"} mono />
        <FieldCard label="Coverage Type" value={doc.coverage_type} mono />
        <FieldCard label="Expires" value={doc.expires} mono />
        <FieldCard
          label="Equipment ID"
          value={doc.equipment_id.slice(0, 8) + "…"}
          mono
        />
        <FieldCard
          label="Notes"
          value={doc.notes || "—"}
          className="sm:col-span-2 lg:col-span-3"
        />
      </div>

      {canWrite && (
        <>
          <EditWarrantyModal
            open={editOpen}
            onClose={() => setEditOpen(false)}
            warranty={doc}
            onSaved={(d) => {
              setEditOpen(false);
              setDoc({ ...doc, ...d });
            }}
          />
          <ConfirmDelete
            open={delOpen}
            onClose={() => setDelOpen(false)}
            onConfirm={handleDelete}
            label="Warranty"
            busy={delBusy}
          />
        </>
      )}
    </Layout>
  );
}

function EditWarrantyModal({ open, onClose, warranty, onSaved }) {
  const [form, setForm] = useState({});
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        manufacturer: warranty.manufacturer,
        platform_type: warranty.platform_type || "",
        coverage_type: warranty.coverage_type,
        expires: warranty.expires,
        notes: warranty.notes || "",
      });
      setErr("");
    }
  }, [open, warranty]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const { data } = await api.patch(`/warranties/${warranty.id}`, {
        manufacturer: form.manufacturer,
        platform_type: form.platform_type || null,
        coverage_type: form.coverage_type,
        expires: form.expires,
        notes: form.notes || null,
      });
      onSaved(data);
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit Warranty"
      subtitle="update record"
      data-testid="warranty-edit-modal"
    >
      <form onSubmit={submit} className="space-y-4">
        <Field label="Manufacturer" required>
          <TextInput
            required
            value={form.manufacturer}
            onChange={(v) => setForm({ ...form, manufacturer: v })}
          />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Platform Type">
            <Select
              value={form.platform_type}
              onChange={(v) => setForm({ ...form, platform_type: v || "" })}
              options={PLATFORM_TYPES}
              placeholder="None"
            />
          </Field>
          <Field label="Coverage" required>
            <Select
              value={form.coverage_type}
              onChange={(v) =>
                setForm({ ...form, coverage_type: v || "full" })
              }
              options={COVERAGE_TYPES}
              placeholder=""
            />
          </Field>
        </div>
        <Field label="Expires" required>
          <DateInput
            required
            value={form.expires}
            onChange={(v) => setForm({ ...form, expires: v })}
          />
        </Field>
        <Field label="Notes">
          <TextArea
            value={form.notes}
            onChange={(v) => setForm({ ...form, notes: v })}
          />
        </Field>
        <ErrorBanner message={err} />
        <div className="flex justify-end gap-2 pt-2">
          <SecondaryButton type="button" onClick={onClose}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            type="submit"
            disabled={busy}
            data-testid="warranty-edit-submit"
          >
            {busy ? "Saving…" : "Save"}
          </PrimaryButton>
        </div>
      </form>
    </Modal>
  );
}
